import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  LayoutDashboard, BarChart3, FileText, CreditCard, Settings, LogOut,
  Menu, X, Users, Eye, DollarSign, Target, RefreshCw, Search,
  Monitor, Smartphone, Tablet, Globe, Clock, CheckCircle, ArrowUpRight,
  ArrowDownRight, Zap, Loader2, Trash2, Bell, TrendingUp,
  User, Mail, Lock, Briefcase, Shield, ChevronLeft, ChevronRight,
  ArrowUp, ArrowDown, Activity, Hash, Calendar, MapPin, Cpu, MessageCircle, Send
} from 'lucide-react';
import { io } from 'socket.io-client';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import toast from 'react-hot-toast';
import logo from '../assets/logo.png';

const statusColors = {
  pending: 'bg-yellow-400/10 text-yellow-400 border border-yellow-400/20',
  processing: 'bg-blue-400/10 text-blue-400 border border-blue-400/20',
  completed: 'bg-green-400/10 text-green-400 border border-green-400/20',
  cancelled: 'bg-red-400/10 text-red-400 border border-red-400/20',
};

const statusDot = {
  pending: 'bg-yellow-400',
  processing: 'bg-blue-400',
  completed: 'bg-green-400',
  cancelled: 'bg-red-400',
};

const deviceIcons = { desktop: Monitor, mobile: Smartphone, tablet: Tablet };

const allNavItems = [
  { id: 'dashboard', label: 'Overview', icon: LayoutDashboard, roles: ['admin', 'superadmin'] },
  { id: 'requests', label: 'Requests', icon: FileText, roles: ['admin', 'superadmin'] },
  { id: 'payments', label: 'Payments', icon: CreditCard, roles: ['admin', 'superadmin'] },
  { id: 'email', label: 'Email', icon: Mail, roles: ['admin', 'superadmin'] },
  { id: 'analytics', label: 'Analytics', icon: BarChart3, roles: ['admin', 'superadmin'] },
  { id: 'audit', label: 'Audit Logs', icon: Shield, roles: ['admin', 'superadmin'] },
  { id: 'chat', label: 'Live Chat', icon: MessageCircle, roles: ['admin', 'superadmin'] },
  { id: 'jobs', label: 'Background Jobs', icon: Cpu, roles: ['admin', 'superadmin'] },
  { id: 'admins', label: 'Admin Management', icon: Users, roles: ['superadmin'] },
  { id: 'performance', label: 'Admin Performance', icon: TrendingUp, roles: ['superadmin'] },
  { id: 'settings', label: 'Settings', icon: Settings, roles: ['admin', 'superadmin'] },
];

const fadeIn = { initial: { opacity: 0, y: 12 }, animate: { opacity: 1, y: 0 }, exit: { opacity: 0, y: -8 } };

const Card = ({ children, className = '' }) => (
  <div className={`bg-gray-900/50 border border-gray-800/60 rounded-2xl ${className}`}>{children}</div>
);

const SectionHeader = ({ title, subtitle, action }) => (
  <div className="flex items-center justify-between mb-5">
    <div>
      <h3 className="text-base font-semibold text-white">{title}</h3>
      {subtitle && <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>}
    </div>
    {action}
  </div>
);

const Pagination = ({ page, total, limit, onChange }) => {
  const pages = Math.ceil(total / limit);
  if (total === 0) return null;
  const start = (page - 1) * limit + 1;
  const end = Math.min(page * limit, total);

  const getPages = () => {
    const arr = [];
    for (let i = 1; i <= pages; i++) {
      if (i === 1 || i === pages || (i >= page - 2 && i <= page + 2)) arr.push(i);
      else if (arr[arr.length - 1] !== '...') arr.push('...');
    }
    return arr;
  };

  return (
    <div className="flex items-center justify-between px-4 py-3 bg-gray-900/80 border-t border-gray-800/60 rounded-b-2xl">
      <span className="text-xs text-gray-500">Showing {start}-{end} of {total}</span>
      {pages > 1 && (
        <div className="flex gap-1">
          <button onClick={() => onChange(page - 1)} disabled={page === 1}
            className="px-2 py-1 rounded text-xs text-gray-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-all">
            Prev
          </button>
          {getPages().map((p, i) => p === '...' ? (
            <span key={`e${i}`} className="px-2 py-1 text-xs text-gray-600">...</span>
          ) : (
            <button key={p} onClick={() => onChange(p)}
              className={`px-2.5 py-1 rounded text-xs font-medium transition-all ${p === page ? 'bg-primary-500 text-white' : 'text-gray-400 hover:text-white hover:bg-gray-800/50'}`}>
              {p}
            </button>
          ))}
          <button onClick={() => onChange(page + 1)} disabled={page === pages}
            className="px-2 py-1 rounded text-xs text-gray-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-all">
            Next
          </button>
        </div>
      )}
    </div>
  );
};

const urlBase64ToUint8Array = (base64String) => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
};

const AdminDashboard = () => {
  const { user, loading: authLoading, logout } = useAuth();
  const navigate = useNavigate();
  const [activePage, setActivePage] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [requests, setRequests] = useState([]);
  const [payments, setPayments] = useState([]);
  const [trafficSources, setTrafficSources] = useState([]);
  const [deviceData, setDeviceData] = useState(null);
  const [pageViews, setPageViews] = useState([]);
  const [topPages, setTopPages] = useState([]);
  const [funnel, setFunnel] = useState([]);
  const [geoData, setGeoData] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [period, setPeriod] = useState('7d');
  const [paymentStats, setPaymentStats] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditPage, setAuditPage] = useState(1);
  const [auditTotal, setAuditTotal] = useState(0);
  const [auditAction, setAuditAction] = useState('');
  const [jobs, setJobs] = useState([]);
  const [jobPage, setJobPage] = useState(1);
  const [queueLength, setQueueLength] = useState(0);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [chatRooms, setChatRooms] = useState([]);
  const [activeRoom, setActiveRoom] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [admins, setAdmins] = useState([]);
  const [adminPerformance, setAdminPerformance] = useState([]);
  const [perfPeriod, setPerfPeriod] = useState('all');
  const [newAdmin, setNewAdmin] = useState({ name: '', email: '', password: '', role: 'admin' });
  const [settingsName, setSettingsName] = useState('');
  const [settingsEmail, setSettingsEmail] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPasswordVal, setNewPasswordVal] = useState('');
  const [storeConfig, setStoreConfig] = useState(null);
  const [saving, setSaving] = useState(false);
  const [totalUnread, setTotalUnread] = useState(0);
  const [subscribers, setSubscribers] = useState([]);
  const [emailForm, setEmailForm] = useState({ to: '', subject: '', message: '', bulk: false });
  const [sendingEmail, setSendingEmail] = useState(false);
  const [reqPage, setReqPage] = useState(1);
  const [payPage, setPayPage] = useState(1);
  const [showCreateAccount, setShowCreateAccount] = useState(false);
  const [accountForm, setAccountForm] = useState({
    username: '', password: '', email: '', emailPassword: '', notes: ''
  });
  const [creatingAccount, setCreatingAccount] = useState(false);
  const PAGE_LIMIT = 15;
  const socketRef = useRef(null);

  const subscribeToPush = useCallback(async () => {
    try {
      if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
      const reg = await navigator.serviceWorker.ready;
      const res = await api.get('/admin/vapid-public-key');
      const publicKey = res.data.publicKey;
      if (!publicKey) return;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      });
      await api.post('/admin/push-subscription', { subscription: sub.toJSON() });
    } catch (err) {
      console.error('Push subscribe failed:', err);
    }
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (user?.role !== 'admin' && user?.role !== 'superadmin') { navigate('/login'); return; }
    fetchAllData();
    fetchSubscribers();
    subscribeToPush();

    const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || window.location.origin;
    const socket = io(SOCKET_URL, { transports: ['websocket', 'polling'] });
    socketRef.current = socket;

    socket.on('connect', () => {
      socket.emit('join_room', 'admin');
    });

    socket.on('new_guest_message', (data) => {
      toast(`${data.senderName}: ${data.message.substring(0, 50)}`, { icon: '💬' });
      fetchChatRooms();
      if (data.unreadCount) setTotalUnread(prev => prev + 1);
    });

    socket.on('receive_message', (data) => {
      if (data.senderRole !== 'admin') {
        setChatMessages(prev => [...prev, {
          _id: Date.now().toString(),
          senderName: data.senderName || data.sender || 'Guest',
          senderRole: data.senderRole || 'visitor',
          message: data.message,
          createdAt: data.timestamp || new Date().toISOString(),
        }]);
      }
    });

    socket.on('unread_updated', (data) => {
      fetchChatRooms();
      if (data.totalUnread !== undefined) setTotalUnread(data.totalUnread);
    });

    return () => { socket.disconnect(); };
  }, [user, authLoading, navigate]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const res = await Promise.allSettled([
        api.get('/admin/stats'),
        api.get('/requests'),
        api.get('/analytics/overview'),
        api.get('/analytics/sources'),
        api.get('/analytics/devices'),
        api.get(`/analytics/pageviews?period=${period}`),
        api.get('/analytics/top-pages'),
        api.get('/analytics/funnel'),
        api.get('/analytics/geo'),
        api.get('/payments'),
        api.get('/payments/stats'),
      ]);
      const get = (i) => res[i].status === 'fulfilled' ? res[i].value.data : null;
      setStats(get(0)?.stats);
      setRequests(get(1)?.requests || []);
      setAnalytics(get(2));
      setTrafficSources(get(3)?.sources || []);
      setDeviceData(get(4));
      setPageViews(get(5)?.pageViews || []);
      setTopPages(get(6)?.topPages || []);
      setFunnel(get(7)?.funnel || []);
      setGeoData(get(8));
      setPayments(get(9)?.payments || []);
      setPaymentStats(get(10)?.stats);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const subscribeToPushNotifications = async () => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
    try {
      const reg = await navigator.serviceWorker.ready;
      const res = await api.get('/admin/vapid-public-key');
      const publicKey = res.data.publicKey;
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') return;
      const subscription = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      });
      await api.post('/admin/push-subscription', { subscription });
    } catch (err) { console.error('Push subscription failed:', err); }
  };

  useEffect(() => { subscribeToPushNotifications(); }, []);

  const fetchAuditLogs = useCallback(async (page = 1, action = '') => {
    try {
      const params = new URLSearchParams({ page, limit: 50 });
      if (action) params.append('action', action);
      const res = await api.get(`/admin/audit-logs?${params}`);
      setAuditLogs(res.data.logs || []);
      setAuditTotal(res.data.total || 0);
      setAuditPage(page);
    } catch (e) { console.error(e); }
  }, []);

  const fetchJobs = useCallback(async () => {
    try {
      const res = await api.get('/admin/jobs');
      setJobs(res.data.jobs || []);
      setQueueLength(res.data.queueLength || 0);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => {
    if (activePage === 'audit') fetchAuditLogs(1, auditAction);
    if (activePage === 'jobs') fetchJobs();
    if (activePage === 'chat') fetchChatRooms();
    if (activePage === 'admins') fetchAdmins();
    if (activePage === 'performance') fetchAdminPerformance();
    if (activePage === 'settings') {
      api.get('/store-config').then((res) => setStoreConfig(res.data.config)).catch(() => {});
      setSettingsName(user?.name || '');
      setSettingsEmail(user?.email || '');
    }
  }, [activePage, auditAction, fetchAuditLogs, fetchJobs, user]);

  const fetchChatRooms = useCallback(async () => {
    try {
      const res = await api.get('/chat/rooms');
      setChatRooms(res.data.rooms || []);
    } catch (e) { console.error(e); }
  }, []);

  const fetchMessages = useCallback(async (room) => {
    try {
      const res = await api.get(`/chat/${room}`);
      setChatMessages(res.data.messages || []);

      // Join the room via socket
      if (socketRef.current) {
        socketRef.current.emit('join_chat', { room, adminId: user?.id });
      }

      // Mark messages as read
      await api.put(`/chat/read/${room}`);
      fetchChatRooms();
    } catch (e) { console.error(e); }
  }, [user]);

  const switchRoom = useCallback((room) => {
    // Leave previous room
    if (activeRoom && socketRef.current) {
      socketRef.current.emit('leave_chat', { room: activeRoom });
    }
    setActiveRoom(room);
    fetchMessages(room);
  }, [activeRoom, fetchMessages]);

  const sendAdminMessage = async () => {
    if (!chatInput.trim() || !activeRoom) return;
    const msg = chatInput.trim();
    setChatInput('');
    try {
      // Server controller handles DB save + socket emit to room
      await api.post('/chat', { message: msg, room: activeRoom });
      // Add to local state immediately (optimistic)
      setChatMessages(prev => [...prev, {
        _id: Date.now().toString(),
        senderName: user?.name || 'Admin',
        senderRole: 'admin',
        message: msg,
        createdAt: new Date().toISOString(),
      }]);
      fetchChatRooms();
    } catch (e) { console.error(e); }
  };

  const fetchAdmins = useCallback(async () => {
    try {
      const res = await api.get('/admin/admins');
      setAdmins(res.data.admins || []);
    } catch (e) { console.error(e); }
  }, []);

  const fetchAdminPerformance = useCallback(async (period = 'all') => {
    try {
      const res = await api.get(`/admin/admin-performance?period=${period}`);
      setAdminPerformance(res.data.performance || []);
    } catch (e) { console.error(e); }
  }, []);

  const fetchSubscribers = useCallback(async () => {
    try {
      const res = await api.get('/newsletter');
      setSubscribers(res.data.subscribers || []);
    } catch (e) { console.error(e); }
  }, []);

  const sendEmailToSubscribers = async () => {
    if (!emailForm.subject || !emailForm.message) return;
    setSendingEmail(true);
    try {
      if (emailForm.bulk) {
        const res = await api.post('/newsletter/bulk-send', {
          subject: emailForm.subject,
          html: `<div style="font-family:Arial,sans-serif;color:#e5e7eb;line-height:1.8;font-size:15px">${emailForm.message.replace(/\n/g, '<br/>')}</div>`,
        });
        toast.success(`Sent to ${res.data.sent} subscribers`);
      } else {
        if (!emailForm.to) return;
        await api.post('/newsletter/send', {
          to: emailForm.to,
          subject: emailForm.subject,
          html: `<div style="font-family:Arial,sans-serif;color:#e5e7eb;line-height:1.8;font-size:15px">${emailForm.message.replace(/\n/g, '<br/>')}</div>`,
        });
        toast.success('Email sent');
      }
      setEmailForm({ to: '', subject: '', message: '', bulk: false });
    } catch (e) {
      toast.error('Failed to send email');
    }
    setSendingEmail(false);
  };

  const createAdmin = async () => {
    if (!newAdmin.name || !newAdmin.email || !newAdmin.password) return;
    try {
      await api.post('/admin/admins', newAdmin);
      setNewAdmin({ name: '', email: '', password: '', role: 'admin' });
      fetchAdmins();
    } catch (e) { alert(e.response?.data?.message || 'Failed to create admin'); }
  };

  const deleteAdmin = async (id) => {
    if (!window.confirm('Delete this admin?')) return;
    try {
      await api.delete(`/admin/admins/${id}`);
      fetchAdmins();
    } catch (e) { alert(e.response?.data?.message || 'Failed to delete admin'); }
  };

  const updateStatus = async (id, status) => {
    try {
      await api.put(`/requests/${id}`, { status });
      setRequests(p => p.map(r => r._id === id ? { ...r, status } : r));
      toast.success('Status updated');
    } catch { toast.error('Failed to update'); }
  };

  const createAccount = async () => {
    if (!accountForm.username || !accountForm.password) {
      toast.error('Username and password are required');
      return;
    }
    setCreatingAccount(true);
    try {
      const res = await api.post(`/requests/${selectedRequest._id}/create-account`, accountForm);
      toast.success('Account created and email sent to customer!');
      setRequests(p => p.map(r => r._id === selectedRequest._id ? res.data.request : r));
      setSelectedRequest(res.data.request);
      setShowCreateAccount(false);
      setAccountForm({ username: '', password: '', email: '', emailPassword: '', notes: '' });
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to create account');
    }
    setCreatingAccount(false);
  };

  const deleteRequest = async (id) => {
    if (!window.confirm('Are you sure you want to delete this request?')) return;
    try {
      await api.delete(`/requests/${id}`);
      setRequests(p => p.filter(r => r._id !== id));
      toast.success('Request deleted');
    } catch { toast.error('Failed to delete'); }
  };

  const handleLogout = () => { logout(); navigate('/'); };

  const filteredRequests = requests.filter(r => {
    const term = searchTerm.toLowerCase();
    const matchSearch = !term ||
      r.fullName?.toLowerCase().includes(term) ||
      r.email?.toLowerCase().includes(term) ||
      r.username?.toLowerCase().includes(term) ||
      r.desiredCountry?.toLowerCase().includes(term);
    const matchStatus = statusFilter === 'all' || r.status === statusFilter;
    return matchSearch && matchStatus;
  });

  const paginatedRequests = filteredRequests.slice((reqPage - 1) * PAGE_LIMIT, reqPage * PAGE_LIMIT);

  const todayStats = stats?.today || {};

  if (authLoading || loading) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-950">
      <div className="flex flex-col items-center gap-3">
        <Loader2 className="animate-spin text-primary-400" size={32} />
        <p className="text-sm text-gray-500">Loading dashboard...</p>
      </div>
    </div>
  );

  // ─── OVERVIEW ───────────────────────────────────────────
  const renderDashboard = () => (
    <div className="space-y-4 sm:space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        {[
          { icon: Eye, label: 'Page Views', value: analytics?.totalViews || 0, color: 'text-blue-400', bg: 'bg-blue-400/10' },
          { icon: Users, label: 'Visitors', value: analytics?.uniqueVisitors || 0, color: 'text-purple-400', bg: 'bg-purple-400/10' },
          { icon: FileText, label: 'Total Requests', value: stats?.totalRequests || 0, color: 'text-cyan-400', bg: 'bg-cyan-400/10' },
          { icon: DollarSign, label: 'Total Revenue', value: `₦${(stats?.totalRevenue || 0).toLocaleString()}`, color: 'text-green-400', bg: 'bg-green-400/10' },
        ].map((s, i) => (
          <motion.div key={i} {...fadeIn} transition={{ delay: i * 0.05 }}
            className="bg-gray-900/50 border border-gray-800/60 rounded-2xl p-3 sm:p-5 hover:border-gray-700/60 transition-all">
            <div className="flex items-center justify-between mb-2 sm:mb-3">
              <div className={`w-8 h-8 sm:w-10 sm:h-10 ${s.bg} rounded-xl flex items-center justify-center`}>
                <s.icon size={16} className={s.color} />
              </div>
            </div>
            <p className="text-lg sm:text-2xl font-bold text-white">{s.value}</p>
            <p className="text-[10px] sm:text-xs text-gray-500 mt-1">{s.label}</p>
          </motion.div>
        ))}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        {[
          { icon: Target, label: 'Conversion Rate', value: `${analytics?.conversionRate || 0}%`, color: 'text-yellow-400', bg: 'bg-yellow-400/10' },
          { icon: Zap, label: 'Successful Payments', value: analytics?.totalPayments || 0, color: 'text-emerald-400', bg: 'bg-emerald-400/10' },
          { icon: Clock, label: 'Pending Requests', value: stats?.pendingRequests || 0, color: 'text-orange-400', bg: 'bg-orange-400/10' },
          { icon: CheckCircle, label: 'Completed', value: stats?.completedRequests || 0, color: 'text-green-400', bg: 'bg-green-400/10' },
        ].map((s, i) => (
          <motion.div key={i} {...fadeIn} transition={{ delay: i * 0.05 + 0.2 }}
            className="bg-gray-900/50 border border-gray-800/60 rounded-2xl p-3 sm:p-5 hover:border-gray-700/60 transition-all">
            <div className="flex items-center justify-between mb-2 sm:mb-3">
              <div className={`w-8 h-8 sm:w-10 sm:h-10 ${s.bg} rounded-xl flex items-center justify-center`}>
                <s.icon size={16} className={s.color} />
              </div>
            </div>
            <p className="text-lg sm:text-2xl font-bold text-white">{s.value}</p>
            <p className="text-[10px] sm:text-xs text-gray-500 mt-1">{s.label}</p>
          </motion.div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="p-4 sm:p-6">
          <SectionHeader title="Today's Summary" subtitle={new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })} />
          <div className="grid grid-cols-2 gap-3 sm:gap-4">
            {[
              { label: 'Views', value: todayStats.views || 0, icon: Eye, color: 'text-blue-400' },
              { label: 'Requests', value: todayStats.requests || 0, icon: FileText, color: 'text-cyan-400' },
              { label: 'Payments', value: todayStats.payments || 0, icon: Zap, color: 'text-green-400' },
              { label: 'Revenue', value: `₦${(todayStats.revenue || 0).toLocaleString()}`, icon: DollarSign, color: 'text-emerald-400' },
            ].map((s, i) => (
              <div key={i} className="flex items-center gap-3 p-3 bg-gray-800/30 rounded-xl">
                <div className={`w-9 h-9 ${s.color.replace('text', 'bg')}/10 rounded-lg flex items-center justify-center`}>
                  <s.icon size={16} className={s.color} />
                </div>
                <div>
                  <p className="text-lg font-bold text-white">{s.value}</p>
                  <p className="text-xs text-gray-500">{s.label}</p>
                </div>
              </div>
            ))}
          </div>
        </Card>

        <Card className="p-6">
          <SectionHeader title="Request Status" subtitle="All time breakdown" />
          <div className="space-y-3">
            {[
              { label: 'Pending', value: stats?.pendingRequests || 0, color: 'bg-yellow-400' },
              { label: 'Processing', value: stats?.processingRequests || 0, color: 'bg-blue-400' },
              { label: 'Completed', value: stats?.completedRequests || 0, color: 'bg-green-400' },
              { label: 'Cancelled', value: stats?.cancelledRequests || 0, color: 'bg-red-400' },
            ].map((s, i) => {
              const total = (stats?.totalRequests || 1);
              const pct = ((s.value / total) * 100).toFixed(0);
              return (
                <div key={i}>
                  <div className="flex justify-between items-center mb-1">
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${s.color}`} />
                      <span className="text-sm text-gray-300">{s.label}</span>
                    </div>
                    <span className="text-sm text-gray-500">{s.value} ({pct}%)</span>
                  </div>
                  <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
                    <motion.div initial={{ width: 0 }} animate={{ width: `${pct}%` }}
                      transition={{ delay: i * 0.1, duration: 0.5 }}
                      className={`h-full ${s.color} rounded-full`} />
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      </div>

      <Card className="p-6">
        <SectionHeader title="Recent Requests" action={
          <button onClick={() => setActivePage('requests')} className="text-xs text-primary-400 hover:text-primary-300 flex items-center gap-1">
            View all <ArrowUpRight size={12} />
          </button>
        } />
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-gray-500 border-b border-gray-800/60">
                <th className="text-left py-2.5 px-3 font-medium">Customer</th>
                <th className="text-left py-2.5 px-3 font-medium">Country</th>
                <th className="text-left py-2.5 px-3 font-medium">Username</th>
                <th className="text-left py-2.5 px-3 font-medium">Status</th>
                <th className="text-left py-2.5 px-3 font-medium">Date</th>
              </tr>
            </thead>
            <tbody>
              {(stats?.recentRequests || []).slice(0, 5).map(r => (
                <tr key={r._id} className="border-b border-gray-800/30 hover:bg-gray-800/20 transition-colors">
                  <td className="py-3 px-3">
                    <p className="font-medium text-white">{r.fullName}</p>
                    <p className="text-xs text-gray-500">{r.email}</p>
                  </td>
                  <td className="py-3 px-3 text-gray-300">{r.desiredCountry}</td>
                  <td className="py-3 px-3 text-gray-400 text-xs">{r.username || '-'}</td>
                  <td className="py-3 px-3">
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${statusColors[r.status]}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${statusDot[r.status]}`} />
                      {r.status}
                    </span>
                  </td>
                  <td className="py-3 px-3 text-gray-500 text-xs">{new Date(r.createdAt).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );

  // ─── REQUESTS ───────────────────────────────────────────
  const renderRequests = () => (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
        <div className="relative flex-1 w-full">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={16} />
          <input type="text" value={searchTerm} onChange={e => { setSearchTerm(e.target.value); setReqPage(1); }}
            className="w-full bg-gray-800/50 border border-gray-700/50 rounded-xl pl-9 pr-4 py-2.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:border-primary-500/40 transition-all"
            placeholder="Search by name, email, username, country..." />
        </div>
        <div className="flex gap-2 flex-wrap">
          {['all', 'pending', 'processing', 'completed', 'cancelled'].map(s => (
            <button key={s} onClick={() => { setStatusFilter(s); setReqPage(1); }}
              className={`px-3 py-2 rounded-lg text-xs font-medium transition-all capitalize ${statusFilter === s ? 'bg-primary-500/20 text-primary-400 border border-primary-500/30' : 'bg-gray-800/50 text-gray-400 border border-gray-700/50 hover:text-white'}`}>
              {s}
            </button>
          ))}
        </div>
      </div>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-gray-500 border-b border-gray-800/60">
                <th className="text-left py-3 px-4 font-medium">Customer</th>
                <th className="text-left py-3 px-4 font-medium hidden md:table-cell">Email</th>
                <th className="text-left py-3 px-4 font-medium">Country</th>
                <th className="text-left py-3 px-4 font-medium hidden lg:table-cell">Username</th>
                <th className="text-left py-3 px-4 font-medium">Status</th>
                <th className="text-left py-3 px-4 font-medium hidden xl:table-cell">Device</th>
                <th className="text-left py-3 px-4 font-medium hidden sm:table-cell">Date</th>
                <th className="text-left py-3 px-4 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedRequests.length === 0 ? (
                <tr><td colSpan={8} className="py-12 text-center text-gray-500">No requests found</td></tr>
              ) : paginatedRequests.map(r => (
                <tr key={r._id} className="border-b border-gray-800/30 hover:bg-gray-800/20 transition-colors cursor-pointer"
                  onClick={() => setSelectedRequest(r)}>
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-gradient-to-br from-primary-500/20 to-accent-500/20 rounded-lg flex items-center justify-center shrink-0">
                        <User size={14} className="text-primary-400" />
                      </div>
                      <div className="min-w-0">
                        <span className="font-medium text-white block truncate max-w-[120px] sm:max-w-none">{r.fullName || r.username || r.email}</span>
                        <span className="text-xs text-gray-500 md:hidden block truncate max-w-[140px]">{r.email}</span>
                      </div>
                    </div>
                  </td>
                  <td className="py-3 px-4 text-gray-400 text-xs hidden md:table-cell">{r.email}</td>
                  <td className="py-3 px-4">
                    <span className="flex items-center gap-1.5 text-gray-300">
                      <MapPin size={12} className="text-gray-500" />{r.desiredCountry}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-gray-400 text-xs font-mono hidden lg:table-cell">{r.username || '-'}</td>
                  <td className="py-3 px-4" onClick={e => e.stopPropagation()}>
                    <select value={r.status} onChange={e => updateStatus(r._id, e.target.value)}
                      className="bg-gray-800/50 border border-gray-700/50 rounded-lg px-2.5 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-primary-500/40 cursor-pointer text-white">
                      <option value="pending">Pending</option>
                      <option value="processing">Processing</option>
                      <option value="completed">Completed</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </td>
                  <td className="py-3 px-4 text-gray-400 text-xs capitalize hidden xl:table-cell">{r.visitor?.device || '-'}</td>
                  <td className="py-3 px-4 text-gray-500 text-xs hidden sm:table-cell">{new Date(r.createdAt).toLocaleDateString()}</td>
                  <td className="py-3 px-4" onClick={e => e.stopPropagation()}>
                    <button onClick={() => deleteRequest(r._id)}
                      className="text-gray-500 hover:text-red-400 p-1.5 rounded-lg hover:bg-red-400/10 transition-all">
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination page={reqPage} total={filteredRequests.length} limit={PAGE_LIMIT} onChange={setReqPage} />
      </Card>

      {/* Detail Modal */}
      <AnimatePresence>
        {selectedRequest && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
            onClick={() => setSelectedRequest(null)}>
            <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
              className="bg-gray-900 border border-gray-800 rounded-2xl p-6 max-w-lg w-full max-h-[85vh] overflow-y-auto shadow-2xl"
              onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-lg font-semibold text-white">Request Details</h3>
                  <p className="text-xs text-gray-500">{new Date(selectedRequest.createdAt).toLocaleString()}</p>
                </div>
                <button onClick={() => setSelectedRequest(null)}
                  className="w-8 h-8 rounded-lg bg-gray-800 flex items-center justify-center text-gray-400 hover:text-white transition-colors">
                  <X size={16} />
                </button>
              </div>

              <div className="space-y-5">
                <div className="flex items-center gap-4 p-4 bg-gray-800/30 rounded-xl">
                  <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-accent-500 rounded-xl flex items-center justify-center">
                    <User size={20} className="text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-white">{selectedRequest.fullName}</p>
                    <p className="text-sm text-gray-400">{selectedRequest.email}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  {[
                    { label: 'Phone', value: selectedRequest.phone || 'N/A' },
                    { label: 'Username', value: selectedRequest.username || 'N/A' },
                    { label: 'Country', value: selectedRequest.desiredCountry },
                    { label: 'Package', value: 'Create Foreign TikTok' },
                  ].map((item, i) => (
                    <div key={i} className="p-3 bg-gray-800/20 rounded-xl">
                      <p className="text-xs text-gray-500 mb-1">{item.label}</p>
                      <p className="text-sm font-medium text-white">{item.value}</p>
                    </div>
                  ))}
                </div>

                {selectedRequest.specialRequests && (
                  <div className="p-3 bg-gray-800/20 rounded-xl">
                    <p className="text-xs text-gray-500 mb-1">Special Requests</p>
                    <p className="text-sm text-gray-300">{selectedRequest.specialRequests}</p>
                  </div>
                )}

                <div className="border-t border-gray-800/60 pt-4">
                  <p className="text-xs text-gray-500 mb-3 flex items-center gap-1.5">
                    <Activity size={12} /> Visitor Information
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { label: 'Device', value: selectedRequest.visitor?.device || '-' },
                      { label: 'Browser', value: selectedRequest.visitor?.browser || '-' },
                      { label: 'OS', value: selectedRequest.visitor?.os || '-' },
                      { label: 'Source', value: selectedRequest.visitor?.source || 'direct' },
                      { label: 'IP', value: selectedRequest.visitor?.ip || '-' },
                    ].map((item, i) => (
                      <div key={i} className="flex justify-between text-sm">
                        <span className="text-gray-500">{item.label}</span>
                        <span className="text-gray-300">{item.value}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex gap-2 pt-2">
                  {['pending', 'processing', 'completed', 'cancelled'].map(s => (
                    <button key={s} onClick={() => { updateStatus(selectedRequest._id, s); setSelectedRequest({ ...selectedRequest, status: s }); }}
                      className={`flex-1 py-2 rounded-lg text-xs font-medium transition-all capitalize ${
                        selectedRequest.status === s
                          ? `${statusColors[s]}`
                          : 'bg-gray-800/50 text-gray-400 border border-gray-700/50 hover:text-white'
                      }`}>
                      {s}
                    </button>
                  ))}
                </div>

                {selectedRequest.status !== 'completed' && (
                  <button onClick={() => setShowCreateAccount(true)}
                    className="w-full py-3 rounded-xl bg-gradient-to-r from-green-500 to-emerald-500 text-white font-bold text-sm shadow-lg shadow-green-500/25 hover:shadow-xl transition-all">
                    Create TikTok Account
                  </button>
                )}

                {selectedRequest.createdAccount && (
                  <div className="p-4 bg-green-500/10 border border-green-500/20 rounded-xl">
                    <p className="text-xs text-green-400 font-semibold mb-2">Created Account</p>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div><span className="text-gray-500">Username:</span> <span className="text-white">{selectedRequest.createdAccount.username}</span></div>
                      <div><span className="text-gray-500">Password:</span> <span className="text-white">{selectedRequest.createdAccount.password}</span></div>
                      {selectedRequest.createdAccount.email && (
                        <div><span className="text-gray-500">Email:</span> <span className="text-white">{selectedRequest.createdAccount.email}</span></div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Create Account Modal */}
      <AnimatePresence>
        {showCreateAccount && selectedRequest && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
            onClick={() => setShowCreateAccount(false)}>
            <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
              className="bg-gray-900 border border-gray-800 rounded-2xl p-6 max-w-md w-full shadow-2xl"
              onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-white">Create TikTok Account</h3>
                <button onClick={() => setShowCreateAccount(false)}
                  className="w-8 h-8 rounded-lg bg-gray-800 flex items-center justify-center text-gray-400 hover:text-white transition-colors">
                  <X size={16} />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">TikTok Username *</label>
                  <input type="text" value={accountForm.username} onChange={e => setAccountForm({ ...accountForm, username: e.target.value })}
                    className="w-full bg-gray-800/50 border border-gray-700/50 rounded-xl px-4 py-2.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500/40"
                    placeholder="Enter TikTok username" />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">TikTok Password *</label>
                  <input type="text" value={accountForm.password} onChange={e => setAccountForm({ ...accountForm, password: e.target.value })}
                    className="w-full bg-gray-800/50 border border-gray-700/50 rounded-xl px-4 py-2.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500/40"
                    placeholder="Enter TikTok password" />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Associated Email</label>
                  <input type="email" value={accountForm.email} onChange={e => setAccountForm({ ...accountForm, email: e.target.value })}
                    className="w-full bg-gray-800/50 border border-gray-700/50 rounded-xl px-4 py-2.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500/40"
                    placeholder="Enter email used for account" />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Email Password</label>
                  <input type="text" value={accountForm.emailPassword} onChange={e => setAccountForm({ ...accountForm, emailPassword: e.target.value })}
                    className="w-full bg-gray-800/50 border border-gray-700/50 rounded-xl px-4 py-2.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500/40"
                    placeholder="Enter email password" />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Notes</label>
                  <textarea value={accountForm.notes} onChange={e => setAccountForm({ ...accountForm, notes: e.target.value })} rows={2}
                    className="w-full bg-gray-800/50 border border-gray-700/50 rounded-xl px-4 py-2.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500/40 resize-none"
                    placeholder="Any additional notes..." />
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button onClick={() => setShowCreateAccount(false)}
                  className="flex-1 py-2.5 rounded-xl bg-gray-800 text-gray-400 text-sm font-medium hover:text-white transition-all">
                  Cancel
                </button>
                <button onClick={createAccount} disabled={creatingAccount}
                  className="flex-1 py-2.5 rounded-xl bg-gradient-to-r from-green-500 to-emerald-500 text-white text-sm font-bold shadow-lg shadow-green-500/25 disabled:opacity-50 transition-all">
                  {creatingAccount ? <Loader2 size={16} className="animate-spin mx-auto" /> : 'Create & Send'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );

  // ─── PAYMENTS ───────────────────────────────────────────
  const paginatedPayments = payments.slice((payPage - 1) * PAGE_LIMIT, payPage * PAGE_LIMIT);

  const renderPayments = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { icon: DollarSign, label: 'Total Revenue', value: `₦${(paymentStats?.totalRevenue || 0).toLocaleString()}`, color: 'text-green-400', bg: 'bg-green-400/10' },
          { icon: CheckCircle, label: 'Successful', value: paymentStats?.successfulPayments || 0, color: 'text-emerald-400', bg: 'bg-emerald-400/10' },
          { icon: Clock, label: 'Pending', value: paymentStats?.pendingPayments || 0, color: 'text-yellow-400', bg: 'bg-yellow-400/10' },
          { icon: Zap, label: 'This Month', value: paymentStats?.monthlyRevenue?.[0]?.count || 0, color: 'text-blue-400', bg: 'bg-blue-400/10' },
        ].map((s, i) => (
          <motion.div key={i} {...fadeIn} transition={{ delay: i * 0.05 }}
            className="bg-gray-900/50 border border-gray-800/60 rounded-2xl p-5">
            <div className={`w-10 h-10 ${s.bg} rounded-xl flex items-center justify-center mb-3`}>
              <s.icon size={18} className={s.color} />
            </div>
            <p className="text-2xl font-bold text-white">{s.value}</p>
            <p className="text-xs text-gray-500 mt-1">{s.label}</p>
          </motion.div>
        ))}
      </div>

      {paymentStats?.monthlyRevenue?.length > 0 && (
        <Card className="p-6">
          <SectionHeader title="Monthly Revenue" />
          <div className="space-y-3">
            {paymentStats.monthlyRevenue.map((m, i) => (
              <div key={`${m._id.year}-${m._id.month}`}
                className="flex items-center justify-between py-3 border-b border-gray-800/30 last:border-0">
                <span className="text-sm text-gray-300">
                  {new Date(m._id.year, m._id.month - 1).toLocaleString('default', { month: 'long', year: 'numeric' })}
                </span>
                <div className="flex items-center gap-4">
                  <span className="text-sm text-gray-500">{m.count} payments</span>
                  <span className="font-semibold text-white">₦{m.total.toLocaleString()}</span>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      <Card className="overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-800/60">
          <SectionHeader title="Payment History" subtitle={`${payments.length} total payments`} />
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-gray-500 border-b border-gray-800/60">
                <th className="text-left py-3 px-4 font-medium">Customer</th>
                <th className="text-left py-3 px-4 font-medium hidden md:table-cell">Email</th>
                <th className="text-left py-3 px-4 font-medium">Amount</th>
                <th className="text-left py-3 px-4 font-medium">Status</th>
                <th className="text-left py-3 px-4 font-medium hidden lg:table-cell">Reference</th>
                <th className="text-left py-3 px-4 font-medium hidden sm:table-cell">Date</th>
              </tr>
            </thead>
            <tbody>
              {paginatedPayments.length === 0 ? (
                <tr><td colSpan={6} className="py-12 text-center text-gray-500">No payments yet</td></tr>
              ) : paginatedPayments.map(p => (
                <tr key={p._id} className="border-b border-gray-800/30 hover:bg-gray-800/20 transition-colors">
                  <td className="py-3 px-4">
                    <div>
                      <span className="text-white block">{p.request?.fullName || p.request?.username || '-'}</span>
                      <span className="text-xs text-gray-500 md:hidden">{p.request?.email || '-'}</span>
                    </div>
                  </td>
                  <td className="py-3 px-4 text-gray-400 text-xs hidden md:table-cell">{p.request?.email || '-'}</td>
                  <td className="py-3 px-4 font-semibold text-white">₦{p.amount?.toLocaleString()}</td>
                  <td className="py-3 px-4">
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                      p.status === 'success' ? 'bg-green-400/10 text-green-400 border border-green-400/20'
                        : p.status === 'pending' ? 'bg-yellow-400/10 text-yellow-400 border border-yellow-400/20'
                        : 'bg-red-400/10 text-red-400 border border-red-400/20'
                    }`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${p.status === 'success' ? 'bg-green-400' : p.status === 'pending' ? 'bg-yellow-400' : 'bg-red-400'}`} />
                      {p.status}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-gray-500 text-xs font-mono hidden lg:table-cell">{p.flutterwaveReference || '-'}</td>
                  <td className="py-3 px-4 text-gray-500 text-xs hidden sm:table-cell">{new Date(p.createdAt).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination page={payPage} total={payments.length} limit={PAGE_LIMIT} onChange={setPayPage} />
      </Card>
    </div>
  );

  // ─── ANALYTICS ──────────────────────────────────────────
  const renderAnalytics = () => (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        {['24h', '7d', '30d', '90d'].map(p => (
          <button key={p} onClick={() => setPeriod(p)}
            className={`px-4 py-2 rounded-lg text-xs font-medium transition-all ${period === p ? 'bg-primary-500 text-white' : 'bg-gray-800/50 text-gray-400 border border-gray-700/50 hover:text-white'}`}>
            {p}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-6">
          <SectionHeader title="Traffic Sources" subtitle="Last 7 days" />
          {trafficSources.length === 0 ? <p className="text-gray-500 text-center py-8">No data yet</p> : (
            <div className="space-y-3">
              {trafficSources.slice(0, 8).map((s, i) => {
                const max = trafficSources[0]?.views || 1;
                return (
                  <div key={s._id} className="flex items-center gap-3">
                    <span className="text-sm text-gray-400 w-24 truncate">{s._id}</span>
                    <div className="flex-1 bg-gray-800/50 rounded-full h-6 overflow-hidden">
                      <motion.div initial={{ width: 0 }} animate={{ width: `${(s.views / max) * 100}%` }}
                        transition={{ delay: i * 0.05 }}
                        className="h-full bg-gradient-to-r from-primary-500 to-accent-500 rounded-full flex items-center justify-end pr-2">
                        <span className="text-xs text-white font-medium">{s.views}</span>
                      </motion.div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>

        <Card className="p-6">
          <SectionHeader title="Devices" subtitle="All time" />
          {deviceData ? (
            <div className="space-y-6">
              <div>
                <h4 className="text-xs text-gray-500 mb-3 uppercase tracking-wider">By Device</h4>
                <div className="space-y-2">
                  {deviceData.devices.map(d => {
                    const Icon = deviceIcons[d._id] || Monitor;
                    const total = deviceData.devices.reduce((a, b) => a + b.count, 0) || 1;
                    const pct = ((d.count / total) * 100).toFixed(0);
                    return (
                      <div key={d._id} className="flex items-center gap-3">
                        <Icon size={16} className="text-gray-500" />
                        <span className="text-sm text-gray-300 w-16 capitalize">{d._id}</span>
                        <div className="flex-1 bg-gray-800/50 rounded-full h-3 overflow-hidden">
                          <div className="h-full bg-primary-500 rounded-full" style={{ width: `${pct}%` }} />
                        </div>
                        <span className="text-xs text-gray-500 w-10 text-right">{pct}%</span>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div>
                <h4 className="text-xs text-gray-500 mb-3 uppercase tracking-wider">Browsers</h4>
                <div className="space-y-2">
                  {deviceData.browsers.slice(0, 5).map(b => (
                    <div key={b._id} className="flex justify-between py-1">
                      <span className="text-sm text-gray-300">{b._id}</span>
                      <span className="text-sm text-gray-500">{b.count}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <h4 className="text-xs text-gray-500 mb-3 uppercase tracking-wider">Operating Systems</h4>
                <div className="space-y-2">
                  {deviceData.oses.map(o => (
                    <div key={o._id} className="flex justify-between py-1">
                      <span className="text-sm text-gray-300">{o._id}</span>
                      <span className="text-sm text-gray-500">{o.count}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : <p className="text-gray-500 text-center py-8">No data</p>}
        </Card>
      </div>

      <Card className="p-6">
        <SectionHeader title="Page Views Over Time" subtitle={`${period} period`} />
        {pageViews.length === 0 ? <p className="text-gray-500 text-center py-12">No data</p> : (
          <div className="flex items-end gap-1 h-48">
            {pageViews.map((pv, i) => {
              const max = Math.max(...pageViews.map(p => p.views), 1);
              return (
                <motion.div key={pv.date} initial={{ height: 0 }} animate={{ height: `${(pv.views / max) * 100}%` }}
                  transition={{ delay: i * 0.03, duration: 0.3 }}
                  className="flex-1 bg-gradient-to-t from-primary-500 to-accent-500 rounded-t min-w-[6px] relative group cursor-pointer"
                  title={`${pv.date}: ${pv.views} views`}>
                  <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-gray-800 text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                    {pv.views}
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-6">
          <SectionHeader title="Top Countries" icon={Globe} />
          {geoData?.byCountry?.length > 0 ? (
            <div className="space-y-3">
              {geoData.byCountry.slice(0, 6).map((c, i) => {
                const max = geoData.byCountry[0]?.visitors || 1;
                return (
                  <div key={c._id} className="flex items-center gap-3">
                    <span className="text-sm text-gray-400 w-16 truncate">{c._id}</span>
                    <div className="flex-1 bg-gray-800/50 rounded-full h-5 overflow-hidden">
                      <motion.div initial={{ width: 0 }} animate={{ width: `${(c.visitors / max) * 100}%` }}
                        transition={{ delay: i * 0.05 }}
                        className="h-full bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-end pr-2">
                        <span className="text-xs text-white">{c.visitors}</span>
                      </motion.div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : <p className="text-gray-500 text-center py-8">No data</p>}
        </Card>

        <Card className="p-6">
          <SectionHeader title="Top Pages" />
          {topPages.length > 0 ? (
            <div className="space-y-3">
              {topPages.map(p => (
                <div key={p.path} className="flex items-center justify-between py-2 border-b border-gray-800/30 last:border-0">
                  <span className="text-sm font-medium text-white">{p.path}</span>
                  <div className="flex items-center gap-4 text-sm text-gray-500">
                    <span>{p.views} views</span>
                    <span>{p.uniqueVisitors} visitors</span>
                  </div>
                </div>
              ))}
            </div>
          ) : <p className="text-gray-500 text-center py-8">No data</p>}
        </Card>
      </div>

      {funnel.length > 0 && (
        <Card className="p-6">
          <SectionHeader title="Conversion Funnel" />
          <div className="space-y-3 max-w-2xl">
            {funnel.map((step, i) => {
              const max = funnel[0]?.count || 1;
              const pct = (step.count / max) * 100;
              return (
                <div key={step.step}>
                  <div className="flex justify-between mb-1">
                    <span className="text-sm text-gray-300">{step.step}</span>
                    <span className="text-sm text-gray-500">{step.count.toLocaleString()}</span>
                  </div>
                  <div className="bg-gray-800/50 rounded-full h-8 overflow-hidden">
                    <motion.div initial={{ width: 0 }} animate={{ width: `${pct}%` }}
                      transition={{ delay: i * 0.1, duration: 0.5 }}
                      className="h-full bg-gradient-to-r from-primary-500 to-accent-500 rounded-full flex items-center justify-center">
                      {pct > 15 && <span className="text-xs font-semibold text-white">{step.count.toLocaleString()}</span>}
                    </motion.div>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}
    </div>
  );

  // ─── AUDIT LOGS ─────────────────────────────────────────
  const renderAuditLogs = () => {
    const actionTypes = ['', 'page_view', 'form_step', 'fingerprint_capture', 'payment_initiated', 'payment_verified', 'payment_failed'];
    const actionLabels = { '': 'All Actions', page_view: 'Page Views', form_step: 'Form Steps', fingerprint_capture: 'Fingerprints', payment_initiated: 'Payments Initiated', payment_verified: 'Payments Verified', payment_failed: 'Payments Failed' };

    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2 flex-wrap">
          {actionTypes.map(a => (
            <button key={a} onClick={() => { setAuditAction(a); fetchAuditLogs(1, a); }}
              className={`px-3 py-2 rounded-lg text-xs font-medium transition-all ${auditAction === a ? 'bg-primary-500/20 text-primary-400 border border-primary-500/30' : 'bg-gray-800/50 text-gray-400 border border-gray-700/50 hover:text-white'}`}>
              {actionLabels[a]}
            </button>
          ))}
        </div>

        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-gray-500 border-b border-gray-800/60">
                  <th className="text-left py-3 px-4 font-medium">Action</th>
                  <th className="text-left py-3 px-4 font-medium hidden md:table-cell">Entity</th>
                  <th className="text-left py-3 px-4 font-medium hidden lg:table-cell">Actor</th>
                  <th className="text-left py-3 px-4 font-medium hidden lg:table-cell">Details</th>
                  <th className="text-left py-3 px-4 font-medium hidden sm:table-cell">Status</th>
                  <th className="text-left py-3 px-4 font-medium">Time</th>
                </tr>
              </thead>
              <tbody>
                {auditLogs.length === 0 ? (
                  <tr><td colSpan={6} className="py-12 text-center text-gray-500">No audit logs found</td></tr>
                ) : auditLogs.map(log => (
                  <tr key={log._id} className="border-b border-gray-800/30 hover:bg-gray-800/20 transition-colors">
                    <td className="py-3 px-4">
                      <span className="px-2 py-1 rounded-md text-xs font-medium bg-gray-800/50 text-gray-300 border border-gray-700/30">
                        {log.action}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-gray-400 text-xs hidden md:table-cell">{log.entity}</td>
                    <td className="py-3 px-4 text-gray-400 text-xs hidden lg:table-cell">{log.actor?.type || '-'}</td>
                    <td className="py-3 px-4 text-gray-500 text-xs max-w-[200px] truncate hidden sm:table-cell">
                      {log.details ? JSON.stringify(log.details).substring(0, 60) : '-'}
                    </td>
                    <td className="py-3 px-4">
                      <span className={`w-2 h-2 rounded-full inline-block ${log.status === 'failure' ? 'bg-red-400' : 'bg-green-400'}`} />
                    </td>
                    <td className="py-3 px-4 text-gray-500 text-xs">{new Date(log.createdAt).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination page={auditPage} total={auditTotal} limit={50} onChange={(p) => fetchAuditLogs(p, auditAction)} />
        </Card>
      </div>
    );
  };

  // ─── BACKGROUND JOBS ────────────────────────────────────
  const renderJobs = () => {
    const statusIcon = { pending: Clock, processing: Loader2, completed: CheckCircle, failed: X };
    const statusStyle = {
      pending: 'bg-yellow-400/10 text-yellow-400 border border-yellow-400/20',
      processing: 'bg-blue-400/10 text-blue-400 border border-blue-400/20',
      completed: 'bg-green-400/10 text-green-400 border border-green-400/20',
      failed: 'bg-red-400/10 text-red-400 border border-red-400/20',
    };

    return (
      <div className="space-y-4">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label: 'Queue Length', value: queueLength, icon: Activity, color: 'text-blue-400', bg: 'bg-blue-400/10' },
            { label: 'Total Jobs', value: jobs.length, icon: Briefcase, color: 'text-purple-400', bg: 'bg-purple-400/10' },
            { label: 'Completed', value: jobs.filter(j => j.status === 'completed').length, icon: CheckCircle, color: 'text-green-400', bg: 'bg-green-400/10' },
            { label: 'Failed', value: jobs.filter(j => j.status === 'failed').length, icon: X, color: 'text-red-400', bg: 'bg-red-400/10' },
          ].map((s, i) => (
            <motion.div key={i} {...fadeIn} transition={{ delay: i * 0.05 }}
              className="bg-gray-900/50 border border-gray-800/60 rounded-2xl p-5">
              <div className={`w-10 h-10 ${s.bg} rounded-xl flex items-center justify-center mb-3`}>
                <s.icon size={18} className={s.color} />
              </div>
              <p className="text-2xl font-bold text-white">{s.value}</p>
              <p className="text-xs text-gray-500 mt-1">{s.label}</p>
            </motion.div>
          ))}
        </div>

        {(() => {
          const paginatedJobs = jobs.slice((jobPage - 1) * PAGE_LIMIT, jobPage * PAGE_LIMIT);
          return (
            <Card className="overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-800/60 flex items-center justify-between">
                <SectionHeader title="Job History" subtitle="Background job queue and results" />
                <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
                  onClick={fetchJobs}
                  className="p-2 bg-gray-800/50 rounded-lg text-gray-400 hover:text-white border border-gray-700/50 transition-all">
                  <RefreshCw size={16} />
                </motion.button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-gray-500 border-b border-gray-800/60">
                      <th className="text-left py-3 px-4 font-medium">Job ID</th>
                      <th className="text-left py-3 px-4 font-medium">Name</th>
                      <th className="text-left py-3 px-4 font-medium">Status</th>
                      <th className="text-left py-3 px-4 font-medium">Created</th>
                      <th className="text-left py-3 px-4 font-medium">Completed</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedJobs.length === 0 ? (
                      <tr><td colSpan={5} className="py-12 text-center text-gray-500">No jobs recorded yet</td></tr>
                    ) : paginatedJobs.map(job => {
                      const Icon = statusIcon[job.status] || Clock;
                      return (
                        <tr key={job.id} className="border-b border-gray-800/30 hover:bg-gray-800/20 transition-colors">
                          <td className="py-3 px-4 text-gray-400 text-xs font-mono">{job.id?.substring(0, 20)}...</td>
                          <td className="py-3 px-4">
                            <span className="px-2 py-1 rounded-md text-xs font-medium bg-gray-800/50 text-gray-300 border border-gray-700/30">
                              {job.name}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${statusStyle[job.status]}`}>
                              <Icon size={12} className={job.status === 'processing' ? 'animate-spin' : ''} />
                              {job.status}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-gray-500 text-xs">
                            {job.createdAt ? new Date(job.createdAt).toLocaleString() : '-'}
                          </td>
                          <td className="py-3 px-4 text-gray-500 text-xs">
                            {job.completedAt ? new Date(job.completedAt).toLocaleString() : '-'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <Pagination page={jobPage} total={jobs.length} limit={PAGE_LIMIT} onChange={setJobPage} />
            </Card>
          );
        })()}
      </div>
    );
  };

  // ─── SETTINGS ───────────────────────────────────────────
  const renderSettings = () => {
    const saveStoreConfig = async () => {
      setSaving(true);
      try {
        await api.put('/store-config', storeConfig);
        toast.success('Store settings saved!');
      } catch (err) {
        toast.error('Failed to save settings');
      }
      setSaving(false);
    };

    const handleUpdatePassword = async () => {
      if (!currentPassword || !newPasswordVal) {
        toast.error('Please fill in both password fields');
        return;
      }
      if (newPasswordVal.length < 6) {
        toast.error('New password must be at least 6 characters');
        return;
      }
      setSaving(true);
      try {
        await api.put('/auth/update-password', { currentPassword, newPassword: newPasswordVal });
        toast.success('Password updated successfully!');
        setCurrentPassword('');
        setNewPasswordVal('');
      } catch (err) {
        toast.error(err.response?.data?.error || 'Failed to update password');
      }
      setSaving(false);
    };

    const fields = [
      { key: 'bankName', label: 'Bank Name', type: 'text' },
      { key: 'accountName', label: 'Account Name', type: 'text' },
      { key: 'accountNumber', label: 'Account Number', type: 'text' },
      { key: 'price', label: 'Price (NGN)', type: 'number' },
      { key: 'whatsappLink', label: 'WhatsApp / Group Link', type: 'url', placeholder: 'https://chat.whatsapp.com/...' },
      { key: 'contactLink', label: 'Contact Link (WhatsApp/Telegram after payment)', type: 'url', placeholder: 'https://wa.me/1234567890 or https://t.me/username' },
      { key: 'countries', label: 'Available Countries (comma-separated)', type: 'text' },
      { key: 'completionMessage', label: 'Completion Message', type: 'textarea' },
    ];

    return (
      <div className="space-y-6 max-w-2xl">
        <Card className="p-6">
          <SectionHeader title="Admin Profile" />
          <div className="space-y-4">
            <div>
              <label className="block text-xs text-gray-500 mb-2">Name</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={16} />
                <input type="text" value={settingsName} onChange={e => setSettingsName(e.target.value)}
                  className="w-full bg-gray-800/50 border border-gray-700/50 rounded-xl pl-10 pr-4 py-3 text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:border-primary-500/40 transition-all" />
              </div>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-2">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={16} />
                <input type="email" value={settingsEmail} onChange={e => setSettingsEmail(e.target.value)}
                  className="w-full bg-gray-800/50 border border-gray-700/50 rounded-xl pl-10 pr-4 py-3 text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:border-primary-500/40 transition-all" />
              </div>
            </div>
            <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
              onClick={() => toast.success('Profile saved')}
              className="bg-primary-500 hover:bg-primary-600 text-white font-medium py-2.5 px-6 rounded-xl transition-all text-sm">
              Save Profile
            </motion.button>
          </div>
        </Card>

        <Card className="p-6">
          <SectionHeader title="Change Password" subtitle="Update your account password" />
          <div className="space-y-4">
            <div>
              <label className="block text-xs text-gray-500 mb-2">Current Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={16} />
                <input type="password" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)}
                  placeholder="Enter current password"
                  className="w-full bg-gray-800/50 border border-gray-700/50 rounded-xl pl-10 pr-4 py-3 text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:border-primary-500/40 transition-all" />
              </div>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-2">New Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={16} />
                <input type="password" value={newPasswordVal} onChange={e => setNewPasswordVal(e.target.value)}
                  placeholder="Enter new password (min 6 characters)"
                  className="w-full bg-gray-800/50 border border-gray-700/50 rounded-xl pl-10 pr-4 py-3 text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:border-primary-500/40 transition-all" />
              </div>
            </div>
            <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
              onClick={handleUpdatePassword} disabled={saving}
              className="bg-red-500 hover:bg-red-600 text-white font-medium py-2.5 px-6 rounded-xl transition-all text-sm disabled:opacity-50 flex items-center gap-2">
              {saving && <Loader2 size={16} className="animate-spin" />} Update Password
            </motion.button>
          </div>
        </Card>

        {storeConfig && user?.role === 'superadmin' && (
          <Card className="p-6">
            <SectionHeader title="Store Settings" subtitle="Configure bank details, pricing, and more" />
            <div className="space-y-4">
              {fields.map(f => (
                <div key={f.key}>
                  <label className="block text-xs text-gray-500 mb-2">{f.label}</label>
                  {f.type === 'textarea' ? (
                    <textarea value={storeConfig[f.key] || ''}
                      onChange={e => setStoreConfig({ ...storeConfig, [f.key]: e.target.value })}
                      className="w-full bg-gray-800/50 border border-gray-700/50 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:border-primary-500/40 min-h-[80px] resize-none transition-all" />
                  ) : f.key === 'countries' ? (
                    <input type="text" value={(storeConfig[f.key] || []).join(', ')}
                      onChange={e => setStoreConfig({ ...storeConfig, [f.key]: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })}
                      className="w-full bg-gray-800/50 border border-gray-700/50 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:border-primary-500/40 transition-all" />
                  ) : (
                    <input type={f.type} value={storeConfig[f.key] || ''}
                      placeholder={f.placeholder || ''}
                      onChange={e => setStoreConfig({ ...storeConfig, [f.key]: f.type === 'number' ? parseInt(e.target.value) || 0 : e.target.value })}
                      className="w-full bg-gray-800/50 border border-gray-700/50 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:border-primary-500/40 transition-all" />
                  )}
                </div>
              ))}
              <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                onClick={saveStoreConfig} disabled={saving}
                className="bg-accent-500 hover:bg-accent-600 text-white font-medium py-2.5 px-6 rounded-xl transition-all disabled:opacity-50 flex items-center gap-2 text-sm">
                {saving && <Loader2 size={16} className="animate-spin" />} Save Store Settings
              </motion.button>
            </div>
          </Card>
        )}
      </div>
    );
  };

  const renderChat = () => (
    <div>
      <h1 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
        <MessageCircle size={20} /> Live Chat
        {totalUnread > 0 && (
          <span className="ml-2 bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">{totalUnread}</span>
        )}
      </h1>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" style={{ height: '600px' }}>
        {/* Rooms List */}
        <Card className="p-0 overflow-hidden flex flex-col">
          <div className="p-3 border-b border-gray-800">
            <p className="text-xs text-gray-400 mb-1">Guest Conversations</p>
            <p className="text-sm text-gray-500">{chatRooms.length} conversation{chatRooms.length !== 1 ? 's' : ''}</p>
          </div>
          <div className="flex-1 overflow-y-auto">
            {chatRooms.map((room) => (
              <button key={room._id} onClick={() => switchRoom(room._id)}
                className={`w-full text-left p-3 border-b border-gray-800/50 hover:bg-gray-800/50 transition-all ${activeRoom === room._id ? 'bg-primary-500/10 border-l-2 border-l-primary-500' : ''}`}>
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-primary-500/20 flex items-center justify-center text-primary-400 text-sm font-bold flex-shrink-0">
                    {room.senderName?.charAt(0)?.toUpperCase() || room._id?.charAt(0)?.toUpperCase() || '?'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <p className="text-sm text-white truncate">{room.senderName || room._id}</p>
                      {room.unreadCount > 0 && (
                        <span className="bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full flex-shrink-0 ml-2">
                          {room.unreadCount}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 truncate">
                      {room.lastMessage?.message?.substring(0, 40) || 'No messages'}
                    </p>
                    <p className="text-[10px] text-gray-600">{room.messageCount || 0} messages</p>
                  </div>
                </div>
              </button>
            ))}
            {chatRooms.length === 0 && (
              <div className="p-6 text-center text-gray-500 text-sm">No conversations yet</div>
            )}
          </div>
        </Card>

        {/* Chat Window */}
        <Card className="lg:col-span-2 p-0 overflow-hidden flex flex-col">
          {activeRoom ? (
            <>
              <div className="p-3 border-b border-gray-800 flex items-center gap-2">
                <MessageCircle size={16} className="text-primary-400" />
                <span className="text-sm text-white font-medium">{activeRoom}</span>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {chatMessages.map((msg) => (
                  <div key={msg._id} className={`flex ${msg.senderRole === 'admin' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 ${msg.senderRole === 'admin' ? 'bg-primary-500/20 text-white' : 'bg-gray-800/60 text-gray-200'}`}>
                      <p className="text-[11px] text-gray-400 mb-1">{msg.senderName || msg.senderEmail || 'Unknown'} · {new Date(msg.createdAt).toLocaleTimeString()}</p>
                      <p className="text-sm">{msg.message}</p>
                    </div>
                  </div>
                ))}
                {chatMessages.length === 0 && (
                  <div className="text-center text-gray-500 text-sm py-8">No messages yet</div>
                )}
              </div>
              <div className="p-3 border-t border-gray-800 flex gap-2">
                <input value={chatInput} onChange={(e) => setChatInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && sendAdminMessage()}
                  placeholder="Type a reply..."
                  className="flex-1 bg-gray-800/50 border border-gray-700/50 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/40" />
                <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
                  onClick={sendAdminMessage} disabled={!chatInput.trim()}
                  className="bg-primary-500 hover:bg-primary-600 text-white p-2.5 rounded-xl disabled:opacity-50 transition-all">
                  <Send size={18} />
                </motion.button>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-gray-500 text-sm">
              Select a conversation to view messages
            </div>
          )}
        </Card>
      </div>
    </div>
  );

  const renderAdmins = () => (
    <div>
      <h1 className="text-xl font-bold text-white mb-4 flex items-center gap-2"><Users size={20} /> Admin Management</h1>

      {/* Create Admin Form */}
      <Card className="p-5 mb-4">
        <SectionHeader title="Add New Admin" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <input value={newAdmin.name} onChange={(e) => setNewAdmin({ ...newAdmin, name: e.target.value })}
            placeholder="Name" className="bg-gray-800/50 border border-gray-700/50 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/40" />
          <input value={newAdmin.email} onChange={(e) => setNewAdmin({ ...newAdmin, email: e.target.value })}
            placeholder="Email" type="email" className="bg-gray-800/50 border border-gray-700/50 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/40" />
          <input value={newAdmin.password} onChange={(e) => setNewAdmin({ ...newAdmin, password: e.target.value })}
            placeholder="Password" type="password" className="bg-gray-800/50 border border-gray-700/50 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/40" />
          <select value={newAdmin.role} onChange={(e) => setNewAdmin({ ...newAdmin, role: e.target.value })}
            className="bg-gray-800/50 border border-gray-700/50 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/40">
            <option value="admin">Admin</option>
            <option value="superadmin">Super Admin</option>
          </select>
          <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
            onClick={createAdmin}
            className="bg-primary-500 hover:bg-primary-600 text-white font-medium py-2.5 px-6 rounded-xl transition-all text-sm">
            Create Admin
          </motion.button>
        </div>
      </Card>

      {/* Admins List */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-800">
                <th className="text-left text-xs font-medium text-gray-400 uppercase tracking-wider px-5 py-3">Admin</th>
                <th className="text-left text-xs font-medium text-gray-400 uppercase tracking-wider px-5 py-3">Role</th>
                <th className="text-left text-xs font-medium text-gray-400 uppercase tracking-wider px-5 py-3">Joined</th>
                <th className="text-right text-xs font-medium text-gray-400 uppercase tracking-wider px-5 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800/50">
              {admins.map((a) => (
                <tr key={a._id} className="hover:bg-gray-800/20 transition-colors">
                  <td className="px-5 py-3">
                    <p className="text-sm text-white font-medium">{a.name}</p>
                    <p className="text-xs text-gray-500">{a.email}</p>
                  </td>
                  <td className="px-5 py-3">
                    <span className={`text-xs font-medium px-2 py-1 rounded-full ${a.role === 'superadmin' ? 'bg-yellow-500/10 text-yellow-400' : 'bg-blue-500/10 text-blue-400'}`}>
                      {a.role === 'superadmin' ? 'Super Admin' : 'Admin'}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-xs text-gray-400">{new Date(a.createdAt).toLocaleDateString()}</td>
                  <td className="px-5 py-3 text-right">
                    {a._id !== user?.id && (
                      <button onClick={() => deleteAdmin(a._id)} className="text-red-400 hover:text-red-300 text-xs">Delete</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );

  const renderPerformance = () => {
    const periods = [
      { value: '24h', label: '24 Hours' },
      { value: '7d', label: '7 Days' },
      { value: '30d', label: '30 Days' },
      { value: '90d', label: '90 Days' },
      { value: 'all', label: 'All Time' },
    ];

    return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <h1 className="text-xl font-bold text-white flex items-center gap-2"><TrendingUp size={20} /> Admin Performance</h1>
        <div className="flex gap-1 bg-gray-900/50 border border-gray-800/60 rounded-xl p-1">
          {periods.map(p => (
            <button key={p.value}
              onClick={() => { setPerfPeriod(p.value); fetchAdminPerformance(p.value); }}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                perfPeriod === p.value
                  ? 'bg-primary-500 text-white'
                  : 'text-gray-400 hover:text-white hover:bg-gray-800/50'
              }`}>
              {p.label}
            </button>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {adminPerformance.map((admin) => (
          <Card key={admin._id} className="p-5">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-primary-500/20 flex items-center justify-center text-primary-400 font-bold">
                {admin.name?.charAt(0)?.toUpperCase() || '?'}
              </div>
              <div>
                <p className="text-sm text-white font-medium">{admin.name}</p>
                <p className="text-xs text-gray-500">{admin.email}</p>
              </div>
              <span className={`ml-auto text-xs font-medium px-2 py-1 rounded-full ${admin.role === 'superadmin' ? 'bg-yellow-500/10 text-yellow-400' : 'bg-blue-500/10 text-blue-400'}`}>
                {admin.role === 'superadmin' ? 'Super Admin' : 'Admin'}
              </span>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between text-xs">
                <span className="text-gray-400">Total Updates</span>
                <span className="text-white font-medium">{admin.metrics.totalUpdates}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-gray-400">Completed</span>
                <span className="text-emerald-400 font-medium">{admin.metrics.completed}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-gray-400">Processing</span>
                <span className="text-yellow-400 font-medium">{admin.metrics.processing}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-gray-400">Cancelled</span>
                <span className="text-red-400 font-medium">{admin.metrics.cancelled}</span>
              </div>
              <div className="flex justify-between text-xs pt-2 border-t border-gray-800">
                <span className="text-gray-400">Completion Rate</span>
                <span className="text-primary-400 font-bold">{admin.metrics.completionRate}%</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-gray-400">Last Active</span>
                <span className="text-gray-500">{admin.lastActivity ? new Date(admin.lastActivity).toLocaleDateString() : 'Never'}</span>
              </div>
            </div>
          </Card>
        ))}
        {adminPerformance.length === 0 && (
          <div className="col-span-full text-center text-gray-500 py-12">No performance data for this period</div>
        )}
      </div>
    </div>
    );
  };

  const renderEmailPage = () => (
    <div className="space-y-6 max-w-4xl">
      <h1 className="text-xl font-bold text-white flex items-center gap-2"><Mail size={20} /> Email Center</h1>

      {/* Send Email */}
      <Card className="p-6">
        <SectionHeader title="Send Email" subtitle="Send individual or bulk email to subscribers" />
        <div className="space-y-4">
          <div className="flex gap-2 mb-3">
            <button onClick={() => setEmailForm({ ...emailForm, bulk: false })}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${!emailForm.bulk ? 'bg-primary-500 text-white' : 'bg-gray-800 text-gray-400 hover:text-white'}`}>
              Individual
            </button>
            <button onClick={() => setEmailForm({ ...emailForm, bulk: true })}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${emailForm.bulk ? 'bg-primary-500 text-white' : 'bg-gray-800 text-gray-400 hover:text-white'}`}>
              Bulk (All Subscribers)
            </button>
          </div>

          {!emailForm.bulk && (
            <div>
              <label className="block text-xs text-gray-500 mb-2">Recipient Email</label>
              <input type="email" value={emailForm.to} onChange={e => setEmailForm({ ...emailForm, to: e.target.value })}
                placeholder="recipient@email.com"
                className="w-full bg-gray-800/50 border border-gray-700/50 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/40" />
            </div>
          )}

          <div>
            <label className="block text-xs text-gray-500 mb-2">Subject</label>
            <input type="text" value={emailForm.subject} onChange={e => setEmailForm({ ...emailForm, subject: e.target.value })}
              placeholder="Email subject..."
              className="w-full bg-gray-800/50 border border-gray-700/50 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/40" />
          </div>

          <div>
            <label className="block text-xs text-gray-500 mb-2">Message</label>
            <textarea value={emailForm.message} onChange={e => setEmailForm({ ...emailForm, message: e.target.value })}
              placeholder="Write your message here..."
              rows={8}
              className="w-full bg-gray-800/50 border border-gray-700/50 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/40 min-h-[160px] resize-none" />
          </div>

          <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
            onClick={sendEmailToSubscribers} disabled={sendingEmail || !emailForm.subject || !emailForm.message || (!emailForm.bulk && !emailForm.to)}
            className="bg-primary-500 hover:bg-primary-600 text-white font-medium py-2.5 px-6 rounded-xl transition-all disabled:opacity-50 flex items-center gap-2 text-sm">
            {sendingEmail && <Loader2 size={16} className="animate-spin" />}
            {emailForm.bulk ? `Send to All (${subscribers.length})` : 'Send Email'}
          </motion.button>
        </div>
      </Card>

      {/* Subscribers List */}
      <Card className="p-6">
        <SectionHeader title="Newsletter Subscribers" subtitle={`${subscribers.length} active subscribers`} />
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-800">
                <th className="text-left text-xs font-medium text-gray-400 uppercase tracking-wider px-4 py-3">Email</th>
                <th className="text-left text-xs font-medium text-gray-400 uppercase tracking-wider px-4 py-3">Name</th>
                <th className="text-left text-xs font-medium text-gray-400 uppercase tracking-wider px-4 py-3">Source</th>
                <th className="text-left text-xs font-medium text-gray-400 uppercase tracking-wider px-4 py-3">Subscribed</th>
                <th className="text-right text-xs font-medium text-gray-400 uppercase tracking-wider px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800/50">
              {subscribers.map((sub) => (
                <tr key={sub._id} className="hover:bg-gray-800/20 transition-colors">
                  <td className="px-4 py-3 text-sm text-white">{sub.email}</td>
                  <td className="px-4 py-3 text-sm text-gray-400">{sub.name || '-'}</td>
                  <td className="px-4 py-3"><span className="text-xs bg-gray-800 text-gray-400 px-2 py-1 rounded-full">{sub.source}</span></td>
                  <td className="px-4 py-3 text-xs text-gray-500">{new Date(sub.createdAt).toLocaleDateString()}</td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => setEmailForm({ ...emailForm, to: sub.email, bulk: false })}
                      className="text-primary-400 hover:text-primary-300 text-xs mr-3">Email</button>
                    <button onClick={async () => {
                      if (!window.confirm('Remove this subscriber?')) return;
                      await api.delete(`/newsletter/${sub._id}`);
                      fetchSubscribers();
                    }} className="text-red-400 hover:text-red-300 text-xs">Remove</button>
                  </td>
                </tr>
              ))}
              {subscribers.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-500 text-sm">No subscribers yet</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );

  const pages = {
    dashboard: renderDashboard,
    requests: renderRequests,
    payments: renderPayments,
    email: renderEmailPage,
    analytics: renderAnalytics,
    audit: renderAuditLogs,
    chat: renderChat,
    jobs: renderJobs,
    admins: renderAdmins,
    performance: renderPerformance,
    settings: renderSettings,
  };

  return (
    <div className="min-h-screen bg-gray-950 flex">
      {/* Mobile Overlay */}
      <AnimatePresence>
        {sidebarOpen && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setSidebarOpen(false)}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 lg:hidden" />
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-gray-900/95 backdrop-blur-xl border-r border-gray-800/60 flex flex-col transform transition-transform duration-300 lg:transform-none ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}>
        <div className="p-5 border-b border-gray-800/60">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl overflow-hidden shadow-lg shadow-primary-500/20">
                <img src={logo} alt="Logo" className="w-full h-full object-cover" />
              </div>
              <div>
                <h1 className="font-bold text-white">Advance Advert Tv Digital</h1>
                <p className="text-[10px] text-gray-500 uppercase tracking-wider">Admin Panel</p>
              </div>
            </div>
            <button onClick={() => setSidebarOpen(false)} className="lg:hidden text-gray-400 hover:text-white p-1">
              <X size={20} />
            </button>
          </div>
        </div>

        <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
          <p className="text-[10px] text-gray-600 uppercase tracking-wider px-3 pt-3 pb-2">Navigation</p>
          {allNavItems.filter(item => item.roles.includes(user?.role)).map(item => (
            <button key={item.id}
              onClick={() => { setActivePage(item.id); setSidebarOpen(false); }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${
                activePage === item.id
                  ? 'bg-primary-500/15 text-primary-400 border border-primary-500/20'
                  : 'text-gray-400 hover:text-white hover:bg-gray-800/50 border border-transparent'
              }`}>
              <item.icon size={18} />
              <span className="flex-1 text-left">{item.label}</span>
              {item.id === 'chat' && totalUnread > 0 && (
                <span className="bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">{totalUnread}</span>
              )}
            </button>
          ))}
        </nav>

        <div className="p-3 border-t border-gray-800/60">
          <div className="flex items-center gap-3 px-3 py-2 mb-2">
            <div className="w-8 h-8 bg-gradient-to-br from-primary-500/20 to-accent-500/20 rounded-lg flex items-center justify-center">
              <User size={14} className="text-primary-400" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-medium text-white truncate">{user?.name}</p>
              <p className="text-[11px] text-gray-500 truncate">{user?.email}</p>
            </div>
          </div>
          <button onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-red-400 hover:bg-red-500/10 border border-transparent hover:border-red-500/20 transition-all">
            <LogOut size={16} />Logout
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top Bar */}
        <header className="sticky top-0 z-30 bg-gray-950/80 backdrop-blur-xl border-b border-gray-800/60 px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <div className="flex items-center gap-3 sm:gap-4">
            <button onClick={() => setSidebarOpen(true)}
              className="lg:hidden text-gray-400 hover:text-white p-1">
              <Menu size={22} />
            </button>
            <div>
              <h2 className="text-base sm:text-lg font-semibold text-white capitalize">
                {allNavItems.find(n => n.id === activePage)?.label || activePage}
              </h2>
              <p className="text-[10px] sm:text-xs text-gray-500 hidden sm:block">
                {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
              onClick={fetchAllData}
              className="p-2.5 bg-gray-800/50 rounded-xl text-gray-400 hover:text-white border border-gray-700/50 transition-all">
              <RefreshCw size={16} />
            </motion.button>
            <button className="p-2.5 bg-gray-800/50 rounded-xl text-gray-400 hover:text-white border border-gray-700/50 transition-all relative">
              <Bell size={16} />
              {(stats?.pendingRequests || 0) > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full text-[9px] text-white flex items-center justify-center font-bold">
                  {stats.pendingRequests}
                </span>
              )}
            </button>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 p-4 sm:p-6 overflow-y-auto">
          <AnimatePresence mode="wait">
            <motion.div key={activePage}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.15 }}>
              {pages[activePage]()}
            </motion.div>
          </AnimatePresence>
        </main>
      </div>
    </div>
  );
};

export default AdminDashboard;
